import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ProductCard } from '@/components/common/ProductCard';
import { getScanHistory } from '@/lib/api';
import { 
  Scan, 
  Upload, 
  History, 
  Users, 
  AlertTriangle,
  TrendingUp,
  ArrowRight,
  Loader2,
  Building2,
  Crown
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalScans: 0,
    avgScore: 0,
    allergensFound: 0,
    hiddenIngredients: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const history = await getScanHistory();
        setRecentScans(history.slice(0, 5));
        
        // Calculate stats
        if (history.length > 0) {
          const totalScans = history.length;
          const avgScore = Math.round(
            history.reduce((sum, item) => sum + item.health_score, 0) / totalScans
          );
          const allergensFound = history.reduce(
            (sum, item) => sum + (item.allergen_warnings?.length || 0), 0
          );
          const hiddenIngredients = history.reduce(
            (sum, item) => sum + (item.hidden_ingredients?.length || 0), 0
          );
          
          setStats({ totalScans, avgScore, allergensFound, hiddenIngredients });
        }
      } catch (error) {
        console.error('Failed to fetch history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const quickActions = [
    { 
      icon: Scan, 
      title: 'Scan Barcode', 
      description: 'Use your camera to scan',
      href: '/scan',
      color: 'bg-[#5DBDB3]/10 text-[#5DBDB3]',
      testId: 'quick-action-scan'
    },
    { 
      icon: Upload, 
      title: 'Upload Image', 
      description: 'Upload a label photo',
      href: '/upload',
      color: 'bg-[#F08B7D]/10 text-[#F08B7D]',
      testId: 'quick-action-upload'
    },
    { 
      icon: History, 
      title: 'View History', 
      description: 'See past scans',
      href: '/history',
      color: 'bg-[#5DBDB3]/10 text-[#5DBDB3]',
      testId: 'quick-action-history'
    },
    { 
      icon: Building2, 
      title: 'For Business', 
      description: 'API & Team access',
      href: '/subscription',
      color: 'bg-[#F08B7D]/10 text-[#F08B7D]',
      testId: 'quick-action-business'
    },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" data-testid="dashboard-welcome">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground text-lg">
            Ready to scan and discover what's in your food?
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Scans</p>
                  <p className="text-2xl font-bold" data-testid="stat-total-scans">{stats.totalScans}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#5DBDB3]/10 flex items-center justify-center">
                  <Scan className="w-5 h-5 text-[#5DBDB3]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Avg Health Score</p>
                  <p className="text-2xl font-bold" data-testid="stat-avg-score">{stats.avgScore || '-'}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#5DBDB3]/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#5DBDB3]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Allergens Found</p>
                  <p className="text-2xl font-bold" data-testid="stat-allergens">{stats.allergensFound}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#F08B7D]/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#F08B7D]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Hidden Found</p>
                  <p className="text-2xl font-bold" data-testid="stat-hidden">{stats.hiddenIngredients}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action) => (
              <Link key={action.href} to={action.href} data-testid={action.testId}>
                <Card className="border border-border hover:border-primary/30 transition-all duration-200 hover:-translate-y-1 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center mb-4`}>
                      <action.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold mb-1">{action.title}</h3>
                    <p className="text-sm text-muted-foreground">{action.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Scans */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Recent Scans</h2>
            {recentScans.length > 0 && (
              <Link to="/history">
                <Button variant="ghost" className="text-sm">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            )}
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : recentScans.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentScans.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <Card className="border border-dashed border-border">
              <CardContent className="py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <Scan className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No scans yet</h3>
                <p className="text-muted-foreground mb-6">
                  Start scanning products to see your history here
                </p>
                <div className="flex justify-center gap-3">
                  <Link to="/scan">
                    <Button data-testid="start-scan-btn">
                      <Scan className="w-4 h-4 mr-2" />
                      Scan Now
                    </Button>
                  </Link>
                  <Link to="/upload">
                    <Button variant="outline">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Allergen Alert */}
        {user?.allergens?.length === 0 && (
          <Card className="mt-8 border border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800">Set up your allergen alerts</h3>
                  <p className="text-sm text-amber-700">
                    Configure your allergens to get warnings when scanning products
                  </p>
                </div>
                <Link to="/profile">
                  <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                    Configure
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
